// Layer 2 (Engine): shared case-scope predicates for role landings and queues.

import type { Case, CauseCategory, FMEAUpdateReview, RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { isCaseThisShift } from '@/versions/v1/shared/clock.derive';
import {
  ADVISORY_RULES,
  CASE_OUTCOMES,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

const RULE_BY_ID = new Map([...DIAGNOSTIC_RULES, ...ADVISORY_RULES].map((row) => [row.id, row]));

const OPEN_CASE_STATES: readonly Case['state'][] = [
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
];

const CLOSED_CASE_STATES: readonly Case['state'][] = ['approved', 'rejected', 'closed'];

/** Assay-signal cases both CRO and Metallurgist rungs act on (#333). */
export const CROSS_RUNG_CASE_IDS = new Set(['case-as-trend']);

function isOpenCase(caseItem: Case): boolean {
  return OPEN_CASE_STATES.includes(caseItem.state);
}

const EXCURSION_RULE_SUFFIXES = [
  'six-point-trend',
  'beyond-limit',
  'widening-variability',
  'cyclical',
] as const;

function variableIdFromExcursionEventId(eventId: string): string | undefined {
  if (!eventId.startsWith('ex-')) return undefined;
  const rest = eventId.slice(3);
  for (const rule of EXCURSION_RULE_SUFFIXES) {
    const suffix = `-${rule}`;
    if (rest.endsWith(suffix)) return rest.slice(0, -suffix.length);
  }
  return undefined;
}

function subprocessForVariable(variableId: string): string | undefined {
  const seriesId = variableId.replace(/^mv-/, 'rs-');
  return TAG_MAPPING.find((row) => row.seriesId === seriesId)?.subprocessId;
}

function isMetProcessExcursionCase(caseItem: Case): boolean {
  const eventId = caseItem.variationEventId;
  if (!eventId?.startsWith('ex-')) return false;
  const variableId = variableIdFromExcursionEventId(eventId);
  if (!variableId) return false;
  const subprocessId = subprocessForVariable(variableId);
  return Boolean(subprocessId && subprocessId !== 'sp-water');
}

export type ResourcingAsk =
  | 'maintenance window'
  | 'contractor call-out'
  | 'lab rush'
  | 'metallurgist time'
  | 'mine liaison';

const ASK_BY_ASSIGNED: Readonly<Partial<Record<Case['assignedTo'], ResourcingAsk>>> = {
  maintenance: 'maintenance window',
  metallurgist: 'metallurgist time',
  cro: 'metallurgist time',
  operator: 'mine liaison',
  gm: 'maintenance window',
  superintendent: 'maintenance window',
  'ops-supervisor': 'maintenance window',
};

/** Superintendent management-queue resourcing ask from cause category and case facts (R5). */
export function superintendentResourcingAsk(
  caseItem: Case,
  actions: readonly RecommendedAction[] = [],
): ResourcingAsk {
  const category = matchedCauseCategory(caseItem);
  const caseActions = actions.filter((a) => a.caseId === caseItem.id);
  const vendorAction = caseActions.some((a) => /vendor|liner|contractor/i.test(a.text));
  const openLabEvidence = caseItem.evidenceRequests.some(
    (r) => r.status === 'open' && r.kind === 'lab-sample',
  );
  const circuit = caseItem.circuit.toLowerCase();

  if (openLabEvidence) return 'lab rush';
  if (category === 'feed-ore' || circuit.includes('mine')) return 'mine liaison';
  if (vendorAction) return 'contractor call-out';
  if (
    category === 'mechanical-electrical' ||
    category === 'spans-both' ||
    caseItem.assignedTo === 'maintenance'
  ) {
    return 'maintenance window';
  }
  if (
    category === 'metallurgical-process' ||
    circuit.includes('flotation') ||
    circuit.includes('reagent') ||
    caseItem.assignedTo === 'metallurgist'
  ) {
    return 'metallurgist time';
  }

  return ASK_BY_ASSIGNED[caseItem.assignedTo] ?? 'metallurgist time';
}

export function matchedCauseCategory(caseItem: Case): CauseCategory | undefined {
  const event = caseItem.variationEventId
    ? VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId)
    : undefined;
  const ruleId = event?.matchedRules[0]?.ruleId;
  return ruleId ? RULE_BY_ID.get(ruleId)?.causeCategory : undefined;
}

export function isMaintenanceSeatCase(caseItem: Case): boolean {
  if (caseItem.assignedTo === 'maintenance') return true;
  const category = matchedCauseCategory(caseItem);
  if (category === 'mechanical-electrical') {
    return true;
  }
  return false;
}

function isThisShift(caseItem: Case): boolean {
  return isCaseThisShift(caseItem.raisedAt, DEFAULT_CURSOR);
}

function caseIdsForReviews(reviews: readonly FMEAUpdateReview[]): Set<string> {
  const ids = new Set<string>();
  for (const review of reviews) {
    const outcome = CASE_OUTCOMES.find((row) => row.id === review.caseOutcomeId);
    if (outcome) ids.add(outcome.caseId);
  }
  return ids;
}

function managerScopeCase(
  caseItem: Case,
  reviews: readonly FMEAUpdateReview[],
): boolean {
  const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id);
  if (!outcome) return false;
  const linked = reviews.filter((row) => row.caseOutcomeId === outcome.id);
  return linked.some(
    (row) =>
      row.reviewStatus === 'pending' ||
      (row.reviewStatus === 'approved' && row.approvalStatus === 'pending'),
  );
}

export type CasesForRoleOptions = {
  reviews?: readonly FMEAUpdateReview[];
  /** Narrower Metallurgist filter for the Advisory role-screen queue (not the Met landing). */
  advisoryQueue?: boolean;
};

/** Open cases in a role's scope — one resolver for all five Advisory role screens. */
export function casesForRole(
  roleId: PlantRoleId,
  cases: readonly Case[],
  options: CasesForRoleOptions = {},
): Case[] {
  const reviews = options.reviews ?? [];

  if (roleId === 'metallurgist') {
    if (options.advisoryQueue) {
      return cases.filter(
        (caseItem) =>
          isOpenCase(caseItem) &&
          ((caseItem.assignedTo === 'metallurgist' &&
            (caseItem.branch !== 'zero-touch' || caseItem.kind === 'new-pattern')) ||
            CROSS_RUNG_CASE_IDS.has(caseItem.id)),
      );
    }
    return cases.filter((caseItem) => {
      if (!isOpenCase(caseItem)) return false;
      if (isMaintenanceSeatCase(caseItem)) return false;
      if (caseItem.assignedTo === 'metallurgist') return true;
      return isMetProcessExcursionCase(caseItem);
    });
  }

  if (roleId === 'cro' || roleId === 'operator') {
    return cases.filter(
      (caseItem) =>
        isOpenCase(caseItem) &&
        isThisShift(caseItem) &&
        (caseItem.assignedTo === 'cro' ||
          caseItem.assignedTo === 'operator' ||
          CROSS_RUNG_CASE_IDS.has(caseItem.id)),
    );
  }

  if (roleId === 'maintenance') {
    return cases.filter((caseItem) => isMaintenanceSeatCase(caseItem) && isOpenCase(caseItem));
  }

  if (roleId === 'superintendent' || roleId === 'ops-supervisor') {
    return cases.filter(isOpenCase);
  }

  if (roleId === 'gm') {
    const reviewCaseIds = caseIdsForReviews(reviews);
    return cases.filter(
      (caseItem) =>
        !CLOSED_CASE_STATES.includes(caseItem.state) &&
        (managerScopeCase(caseItem, reviews) || reviewCaseIds.has(caseItem.id)),
    );
  }

  return [];
}
